import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./LandingPage2.css";

const LandingPage2 = ({ testimonials }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isHovered) {
        setCurrentSlide((prev) => (prev + 1) % testimonials.length);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isHovered]);

  return (
    <div className="page-bg">
      <motion.h1
        className="testimonial-heading"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        What's It Like Using&nbsp;<span className="highlighted-badge">Shutter</span>
      </motion.h1>

      <div
        className="testimonial-carousel"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="testimonial-card-container"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
          >
            <div className="testimonial-card">
              <div className="testimonial-images">
                {testimonials[currentSlide].images.map((image, imgIndex) => (
                  <motion.img
                    key={imgIndex}
                    src={image}
                    alt={`Testimonial image ${imgIndex + 1}`}
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: imgIndex * 0.1 }}
                  />
                ))}
              </div>
              <div className="testimonial-text">
                <motion.div
                  className="quote-icon"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <i className="fas fa-quote-left"></i>
                </motion.div>
                <motion.p
                  className="testimonial-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  {testimonials[currentSlide].message}
                </motion.p>
                <motion.div
                  className="testimonial-author"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <p className="author-name">{testimonials[currentSlide].author}</p>
                  <p className="author-role">{testimonials[currentSlide].role}</p>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="indicator-dots">
        {testimonials.map((_, index) => (
          <motion.span
            key={index}
            className={`dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => setCurrentSlide(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          ></motion.span>
        ))}
      </div>

      <motion.div
        className="cta-buttons"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <motion.button
          className="demo-btn"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{ width: '300px' }}
          onClick={() => window.open('https://api.leadconnectorhq.com/widget/booking/q47rHzIRXjxEby7Q2zqi', '_blank')}
        >
          Book a Demo
        </motion.button>
        <motion.button
          className="credit-btn"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          style={{ width: '300px' }}
          onClick={() => window.location.href = '/webapp'}
        >
          <i className="fas fa-coins"></i>
          Claim 100 Free Credits
        </motion.button>
      </motion.div>

      <motion.div
        className="stats-wrapper"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1],
          staggerChildren: 0.1
        }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="stats-container">
          {[
            { value: "12X", label: "Faster than other 3D models", icon: "⚡" },
            { value: "4X", label: "Higher conversion rates in sales", icon: "📈" },
            { value: "17X", label: "Cheaper than other 3D modeling", icon: "💰" },
            { value: "29%", label: "More efficiency in Image Generation", icon: "🎯" },
          ].map((item, index) => (
            <motion.div
              className="stat-card"
              key={index}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              whileHover={{
                scale: 1.05,
                y: -10,
                transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
              }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
                ease: [0.16, 1, 0.3, 1]
              }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <motion.div
                className="stat-icon"
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{
                  duration: 0.5,
                  delay: 0.2 + index * 0.1,
                  ease: [0.16, 1, 0.3, 1]
                }}
                viewport={{ once: true }}
              >
                {item.icon}
              </motion.div>
              <motion.p
                className="stat-value"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.3 + index * 0.1,
                  ease: [0.16, 1, 0.3, 1]
                }}
                viewport={{ once: true }}
              >
                {item.value}
              </motion.p>
              <motion.p
                className="stat-label"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.4 + index * 0.1,
                  ease: [0.16, 1, 0.3, 1]
                }}
                viewport={{ once: true }}
              >
                {item.label}
              </motion.p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default LandingPage2;
